import { NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Offcanvas } from 'bootstrap';
import { NgxIntlTelInputModule, SearchCountryField } from 'ngx-intl-tel-input';
import { User } from '../../../models/users.model';
import { Subscription } from 'rxjs';
import { HttpServiceService } from '../../../services/http-service.service';
import { NgxToastrService } from '../../../_services/ngx-toastr/ngx-toastr.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { USER_PERMISSIONS } from '../../../app.constants';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

@Component({
  selector: 'app-owner-add',
  imports: [NgIf, ReactiveFormsModule, NgbModule, NgxIntlTelInputModule, MatRadioModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatSelectModule],
  templateUrl: './owner-add.component.html',
  styleUrl: './owner-add.component.css',
  providers: [provideNativeDateAdapter()]
})
export class OwnerAddComponent implements OnDestroy{
  owner_add_edit: boolean = false; 
  userForm! : FormGroup;  
  SearchCountryField = SearchCountryField;
  today : Date = new Date();
  @ViewChild('addOwnerButton') offcanvas!: ElementRef;
  private offcanvasInstance: Offcanvas | null = null;
  @Output() addOwnerEvent = new EventEmitter();
  private addOwnerSubscription? : Subscription;
  private httpService = inject(HttpServiceService);
  private isEditUser : boolean = false;
  private authService = inject(LocalStorageService);
  constructor(private fb: FormBuilder, private alertService: NgxToastrService){
    this.userForm = this.fb.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      email : ['', [Validators.required,  Validators.email]],
      phone : ['', [Validators.required]],
      alternate_email : [''],
      alternate_phone : [''],
      gender : [''],
      dob : [''],
      driving_license : [''],
      marital_status : [null],
      aadhar : [''],
      pan : [''],
      uid : ['']
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const offcanvasElement = this.offcanvas?.nativeElement;
      if (offcanvasElement) {
        this.offcanvasInstance = Offcanvas.getOrCreateInstance(offcanvasElement, {
          backdrop: 'static', 
          keyboard: false,
        });        
      }
    });
  }

  addPropertyEvent(){    
    this.toggleExample();   
    if (this.offcanvasInstance) {
      this.offcanvasInstance.show();
    }
  }

  propertyClose(){
    this.toggleExample();
    if (this.offcanvasInstance) {      
      this.offcanvasInstance.hide();
    }
  }
  
  disableFutureDates = (date: Date | null): boolean => {
    return date ? date <= this.today : false;
  }

  toggleExample() {
    this.owner_add_edit = !this.owner_add_edit;
  }

  editUser(userInfo : any){
    this.toggleExample();
    this.userForm.patchValue({
      ...userInfo,
      ...userInfo.personal_info,
      phone: {
        number: userInfo.personal_info.phone,
        internationalNumber: userInfo.personal_info?.dial_code+' '+userInfo.personal_info.phone,
        nationalNumber: userInfo.personal_info.phone,
        e164Number: userInfo.personal_info?.dial_code+userInfo.personal_info.phone,
        countryCode: userInfo.personal_info?.dial_code ? this.getCountryCodeFromDialCode(userInfo.personal_info?.dial_code) : "",
        dialCode: userInfo.personal_info?.dial_code
      },
      alternate_phone : userInfo.personal_info.alternate_phone ? {
        number: userInfo.personal_info.alternate_phone,
        internationalNumber: userInfo.personal_info?.alt_dial_code+' '+userInfo.personal_info.alternate_phone,
        nationalNumber: userInfo.personal_info.alternate_phone,
        e164Number: userInfo.personal_info?.alt_dial_code+userInfo.personal_info.alternate_phone,
        countryCode: userInfo.personal_info?.alt_dial_code ? this.getCountryCodeFromDialCode(userInfo.personal_info?.alt_dial_code) : "",
        dialCode: userInfo.personal_info?.alt_dial_code
      } : ''
    });
    this.isEditUser = true;
  }

  onSubmit(): void {
    if (this.userForm.valid) {      
      let phone = this.userForm.get("phone")?.value, alternate_phone = this.userForm.get("alternate_phone")?.value ?? "";
      let obj = {
          ...this.userForm.value,
          dial_code : phone.dialCode,
          phone : phone.number?.replace(/[-\s]/g, ""),
          role_identifier : "owner"
      }
      if(alternate_phone){
        obj['alt_dial_code'] = alternate_phone.dialCode;
        obj['alternate_phone'] = alternate_phone.number?.replace(/[-\s]/g, "");
      }        
      const cleanedUser = Object.fromEntries(
          Object.entries(obj).filter(([_, v]) => v !== null && v !== '')
      ) as unknown as User;
      if(this.isEditUser){
        let uid = cleanedUser.uid ?? '';
        delete cleanedUser.uid;
        this.addOwnerSubscription = this.httpService.editUser(cleanedUser, uid).subscribe({
          next : (resp) => {
            this.close();
            this.notifyParent();
            this.alertService.success(resp.message, "toast-top-right");
          }
        })

      }else{
        this.addOwnerSubscription = this.httpService.addUser(cleanedUser).subscribe({
          next : (resp) => {
            this.close();
            this.notifyParent();
            this.alertService.success(resp.message, "toast-top-right");
          }
        })
      }
      
    } else {
      console.log('Form is invalid.');
    }
  }

  close(){
    this.propertyClose();      
    this.userForm.reset();    
    this.isEditUser = false;
  }

  notifyParent(){
    this.addOwnerEvent.emit();
  }

  getCountryCodeFromDialCode(dialCode: string): string | null {
    const code = dialCode.replace('+', '');
    for (const country of getCountries()) {
      if (getCountryCallingCode(country) === code) {
        return country.toLowerCase(); // e.g., 'IN' -> 'in'
      }
    }
    return null;
  }

  ngOnDestroy(): void {
    this.addOwnerSubscription?.unsubscribe();
  }

  get checkUserPersonalInfoUpdate(){
      return this.authService.checkPermission(USER_PERMISSIONS.USER_INFO.USER_PERSONAL_UPDATE);
  }

}
